<template>
  <div class="page">
    <div class="bg-grid" />
    <TopBar />
    <ProjectNav :project="project" />

    <main class="content">
      <!-- No pipeline guard -->
      <div v-if="!has_pipeline" class="no-pipeline">
        <p class="np-title">No active pipeline</p>
        <p class="np-body">Bootstrap your pipeline on the <a :href="`/projects/${project.uuid}/pipeline`" class="np-link">Pipeline tab</a> before testing.</p>
      </div>

      <template v-else>
        <div class="playground-grid">
          <!-- ── Left: input ───────────────────────────────────────── -->
          <div class="input-col">
            <div class="input-header">
              <span class="col-label">INPUT</span>
              <div class="mode-toggle">
                <button
                  class="mode-btn"
                  :class="{ active: inputMode === 'text' }"
                  @click="inputMode = 'text'"
                >TEXT</button>
                <button
                  class="mode-btn"
                  :class="{ active: inputMode === 'json' }"
                  @click="inputMode = 'json'"
                >JSON</button>
              </div>
            </div>

            <!-- Text mode -->
            <template v-if="inputMode === 'text'">
              <textarea
                v-model="inputText"
                class="text-editor"
                placeholder="Ask anything… e.g. How many calories in Coca Cola?"
                spellcheck="true"
              />
              <p class="mode-hint">Sent as <code>{"input": "…"}</code> — use <code>query_key: "input"</code> in your RAG context fields</p>
            </template>

            <!-- JSON mode -->
            <template v-else>
              <textarea
                v-model="inputJson"
                class="json-editor"
                placeholder='{"item": "Arla Øko Sødmælk 1L"}'
                spellcheck="false"
              />
              <p v-if="jsonError" class="json-parse-error">{{ jsonError }}</p>
            </template>

            <div class="client-row">
              <span class="client-label">AI CLIENT</span>
              <span class="client-value">
                <template v-if="mainClient">{{ mainClient.name }} — {{ mainClient.model }}</template>
                <template v-else>No client assigned</template>
              </span>
            </div>

            <button
              class="run-btn"
              :disabled="running || !hasInput || !mainClient"
              @click="runPlayground"
            >
              <span class="run-icon">▶</span>
              {{ running ? 'RUNNING…' : 'RUN PIPELINE' }}
            </button>

            <!-- Webhook opt-in (only shown when project has active webhooks) -->
            <label v-if="has_webhooks" class="webhook-toggle">
              <input
                v-model="fireWebhooks"
                type="checkbox"
                class="webhook-checkbox"
              />
              <span class="webhook-toggle-label">
                <span class="webhook-toggle-icon">📡</span>
                Fire webhooks on this run
              </span>
            </label>

            <p v-if="runError" class="run-error">{{ runError }}</p>
          </div>

          <!-- ── Right: results + feedback + RAG ──────────────────── -->
          <div class="output-col">
            <!-- Idle state -->
            <div v-if="!jobId && !result" class="idle-state">
              <div class="idle-icon">▶</div>
              <p class="idle-text">Results will appear here after you run the pipeline.</p>
            </div>

            <!-- Running -->
            <div v-if="jobId && !result" class="running-state">
              <div class="spinner" />
              <p class="running-text">Pipeline running…</p>
              <JobPoller :job-id="jobId" @done="onDone" @failed="onFailed" />
            </div>

            <!-- Results -->
            <template v-if="result">
              <!-- Step breakdown -->
              <div class="section-label">STEP RESULTS</div>
              <div v-for="step in result.steps" :key="step.step_order" class="step-card">
                <div class="step-head">
                  <div class="step-num">{{ step.step_order }}</div>
                  <span class="step-name">{{ step.step_name }}</span>
                  <span class="step-loops">{{ step.feedback_loops?.length ?? 0 }} loop{{ step.feedback_loops?.length !== 1 ? 's' : '' }}</span>
                  <span v-if="step.rag_hits?.length" class="step-rag-badge">📚 RAG</span>
                  <span class="step-cost">${{ (step.cost ?? 0).toFixed(4) }}</span>
                </div>
                <div v-if="step.feedback_loops?.length" class="step-loops-detail">
                  <span
                    v-for="fl in step.feedback_loops"
                    :key="fl.name"
                    class="fl-chip"
                    :class="{ revised: fl.was_revised }"
                  >
                    {{ fl.name }} · {{ fl.iterations }}×{{ fl.was_revised ? ' ✎' : ' ✓' }}
                  </span>
                </div>

                <!-- RAG hits -->
                <div v-if="step.rag_hits?.length" class="rag-hits">
                  <div class="rag-hits-label">
                    <span class="rag-hits-icon">📚</span>
                    RAG — {{ step.rag_hits.length }} field{{ step.rag_hits.length !== 1 ? 's' : '' }} loaded
                  </div>
                  <div v-for="hit in step.rag_hits" :key="hit.field" class="rag-hit">
                    <div class="rag-hit-meta">
                      <span class="rag-hit-field">{{ hit.field }}</span>
                      <span class="rag-hit-resolver">{{ hit.resolver }}</span>
                      <span class="rag-hit-query">query: "{{ hit.query }}"</span>
                      <span class="rag-hit-count">{{ hit.docs?.length ?? 0 }} doc{{ (hit.docs?.length ?? 0) !== 1 ? 's' : '' }}</span>
                    </div>
                    <div v-if="hit.docs?.length" class="rag-hit-docs">
                      <div v-for="(doc, i) in hit.docs" :key="i" class="rag-hit-doc">{{ doc }}</div>
                    </div>
                    <div v-else class="rag-hit-empty">No matching documents found in knowledge base</div>
                  </div>
                </div>

                <pre class="step-output">{{ formatJson(step.content) }}</pre>
              </div>

              <!-- Final output -->
              <div class="final-output-header">
                <div class="section-label">FINAL OUTPUT</div>
                <span v-if="result.schema_valid === true" class="schema-badge valid">✓ SCHEMA VALID</span>
                <span v-else-if="result.schema_valid === false" class="schema-badge invalid">✗ SCHEMA FAILED ({{ result.schema_attempts }} attempts)</span>
              </div>
              <pre class="final-output">{{ formatJson(result.output) }}</pre>

              <!-- Cost summary -->
              <div class="cost-row">
                <span class="cost-item">{{ result.tokens_in?.toLocaleString() }} tokens in</span>
                <span class="cost-sep">·</span>
                <span class="cost-item">{{ result.tokens_out?.toLocaleString() }} tokens out</span>
                <span class="cost-sep">·</span>
                <span class="cost-item">${{ (result.cost ?? 0).toFixed(4) }}</span>
                <span class="cost-sep">·</span>
                <span class="cost-item">{{ result.processing_ms?.toLocaleString() }}ms</span>
              </div>

              <!-- Webhook notifications (only shown when the run had fire_webhooks enabled) -->
              <div v-if="webhooksFired" class="webhook-notice">
                <span class="webhook-notice-icon">📡</span>
                <div class="webhook-notice-body">
                  <span class="webhook-notice-title">WEBHOOKS FIRED</span>
                  <span class="webhook-notice-text">Active webhooks were notified with a <code>job.completed</code> event.</span>
                </div>
                <a :href="`/projects/${project.uuid}/webhooks`" class="webhook-notice-link">VIEW DELIVERIES →</a>
              </div>

              <!-- Feedback section -->
              <div class="feedback-section">
                <div class="section-label">FEEDBACK</div>
                <div v-if="!feedbackSubmitted" class="feedback-buttons">
                  <button
                    class="fb-btn correct"
                    :disabled="feedbackSaving"
                    :class="{ active: feedbackType === 'thumbs_up' }"
                    @click="submitFeedback('thumbs_up')"
                  >
                    👍 CORRECT
                  </button>
                  <button
                    class="fb-btn wrong"
                    :class="{ active: showCorrection }"
                    @click="showCorrection = !showCorrection"
                  >
                    👎 WRONG
                  </button>
                </div>
                <div v-else class="feedback-done">
                  <span v-if="feedbackType === 'thumbs_up'">👍 Marked correct — positive training signal stored.</span>
                  <span v-else>👎 Correction saved — this becomes a labeled eval case.</span>
                </div>

                <!-- Correction editor -->
                <div v-if="showCorrection && !feedbackSubmitted" class="correction-editor">
                  <label class="field-label">CORRECT ANSWER (JSON)</label>
                  <textarea
                    v-model="correctionJson"
                    class="correction-input"
                    rows="5"
                    placeholder='{"matched_id": 4819, "fat_pct": 0.5}'
                    spellcheck="false"
                  />
                  <p v-if="correctionError" class="correction-error">{{ correctionError }}</p>
                  <button
                    class="correction-submit"
                    :disabled="feedbackSaving || !correctionJson.trim()"
                    @click="submitFeedback('correction')"
                  >
                    {{ feedbackSaving ? 'SUBMITTING…' : 'SUBMIT CORRECTION' }}
                  </button>
                </div>
              </div>

              <!-- RAG learned -->
              <div class="rag-section">
                <button class="rag-toggle" @click="ragOpen = !ragOpen">
                  {{ ragOpen ? '▾' : '▸' }} WHAT THE AI LEARNED
                  <span v-if="ragEntries.length" class="rag-count">{{ ragEntries.length }} entries</span>
                </button>
                <div v-if="ragOpen" class="rag-entries">
                  <div v-if="loadingRag" class="rag-loading">Loading…</div>
                  <div v-else-if="ragEntries.length === 0" class="rag-empty">No RAG entries found. The write job may still be processing — check again in a moment.</div>
                  <div v-else v-for="entry in ragEntries" :key="entry.id" class="rag-entry">
                    <div class="rag-meta">
                      <span class="rag-source-badge" :class="entry.source">{{ entry.source.replace(/_/g, ' ') }}</span>
                      <span class="rag-conf-badge">{{ entry.confidence }}</span>
                    </div>
                    <p class="rag-content">{{ entry.content }}</p>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue"
import TopBar from "../../components/TopBar.vue"
import ProjectNav from "../../components/ProjectNav.vue"
import JobPoller from "../../components/JobPoller.vue"

const props = defineProps({
  project:            { type: Object,  required: true },
  has_pipeline:       { type: Boolean, default: false },
  has_webhooks:       { type: Boolean, default: false },
  project_ai_clients: { type: Array,   default: () => [] },
})

const mainClient = computed(() =>
  props.project_ai_clients.find((c) => c.purpose === "main") ?? props.project_ai_clients[0] ?? null
)

// ── Input mode ────────────────────────────────────────────────────────────────
const inputMode = ref("text")    // "text" | "json"
const inputText = ref("")
const inputJson = ref("")
const jsonError = ref("")

const hasInput = computed(() => {
  if (inputMode.value === "text") return inputText.value.trim().length > 0
  return inputJson.value.trim().length > 0
})

// ── Webhook opt-in ────────────────────────────────────────────────────────────
const fireWebhooks  = ref(false)
const webhooksFired = ref(false)

// ── Run state ─────────────────────────────────────────────────────────────────
const running   = ref(false)
const jobId     = ref(null)
const result    = ref(null)
const messageId = ref(null)
const runError  = ref("")

// ── Feedback state ─────────────────────────────────────────────────────────────
const feedbackType      = ref("")
const feedbackSaving    = ref(false)
const feedbackSubmitted = ref(false)
const showCorrection    = ref(false)
const correctionJson    = ref("")
const correctionError   = ref("")

// ── RAG state ─────────────────────────────────────────────────────────────────
const ragOpen    = ref(false)
const ragEntries = ref([])
const loadingRag = ref(false)

// ── Helpers ───────────────────────────────────────────────────────────────────
function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content
}
async function apiPost(path, body) {
  const res = await fetch(path, {
    method:  "POST",
    headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken() },
    body:    JSON.stringify(body),
  })
  return { status: res.status, data: await res.json() }
}
function formatJson(val) {
  if (!val) return ""
  try { return JSON.stringify(typeof val === "string" ? JSON.parse(val) : val, null, 2) }
  catch { return String(val) }
}
function parsePayload() {
  if (inputMode.value === "text") {
    return { input: inputText.value.trim() }
  }
  jsonError.value = ""
  try {
    return JSON.parse(inputJson.value)
  } catch {
    jsonError.value = "Invalid JSON"
    return null
  }
}

// ── Actions ───────────────────────────────────────────────────────────────────
async function runPlayground() {
  const payload = parsePayload()
  if (!payload) return   // jsonError already set for JSON mode; text mode can't fail

  running.value           = true
  runError.value          = ""
  jsonError.value         = ""
  result.value            = null
  jobId.value             = null
  messageId.value         = null
  webhooksFired.value     = false
  feedbackSubmitted.value = false
  feedbackType.value      = ""
  showCorrection.value    = false
  correctionJson.value    = ""
  ragEntries.value        = []

  try {
    const { status, data } = await apiPost(
      `/api/v1/projects/${props.project.uuid}/test_prompt`,
      { payload, fire_webhooks: fireWebhooks.value }
    )
    if (status === 202) {
      jobId.value = data.job_id
    } else {
      runError.value = data.message || "Failed to start run."
      running.value  = false
    }
  } catch {
    runError.value = "Network error."
    running.value  = false
  }
}

function onDone(jobData) {
  running.value       = false
  jobId.value         = null
  webhooksFired.value = fireWebhooks.value && props.has_webhooks
  const res           = jobData?.result ?? {}
  messageId.value     = res.message_id ?? null
  result.value  = {
    output:          res.output,
    steps:           res.pipeline_steps ?? [],
    tokens_in:       res.tokens_in,
    tokens_out:      res.tokens_out,
    cost:            res.cost,
    processing_ms:   res.processing_ms,
    schema_valid:    res.schema_valid ?? null,
    schema_attempts: res.schema_attempts ?? 0,
  }
}

function onFailed(jobData) {
  running.value  = false
  jobId.value    = null
  runError.value = jobData?.error || "Pipeline run failed."
}

async function submitFeedback(type) {
  if (!messageId.value) { correctionError.value = "No message ID — cannot submit feedback."; return }

  if (type === "correction") {
    let parsed
    try { parsed = JSON.parse(correctionJson.value) }
    catch { correctionError.value = "Correct answer must be valid JSON."; return }
    correctionError.value = ""
    feedbackSaving.value  = true
    try {
      const { status, data } = await apiPost(
        `/api/v1/projects/${props.project.uuid}/messages/${messageId.value}/feedback`,
        { type: "correction", correct_answer: parsed }
      )
      if (status === 201) { feedbackType.value = type; feedbackSubmitted.value = true }
      else correctionError.value = data.message || "Failed to submit."
    } catch { correctionError.value = "Network error." }
    finally { feedbackSaving.value = false }
  } else {
    feedbackSaving.value = true
    try {
      const { status } = await apiPost(
        `/api/v1/projects/${props.project.uuid}/messages/${messageId.value}/feedback`,
        { type }
      )
      if (status === 201) { feedbackType.value = type; feedbackSubmitted.value = true }
    } catch { /* silent */ }
    finally { feedbackSaving.value = false }
  }
}

async function loadRagEntries() {
  loadingRag.value = true
  try {
    const res  = await fetch(`/api/v1/projects/${props.project.uuid}/rag`, {
      headers: { "X-CSRF-Token": csrfToken() }
    })
    const data = await res.json()
    ragEntries.value = (data.documents ?? []).slice(0, 8)
  } catch { /* silent */ }
  finally { loadingRag.value = false }
}

watch(ragOpen, (open) => {
  if (open && result.value && ragEntries.value.length === 0) loadRagEntries()
})
</script>

<style scoped>
.page {
  min-height: 100vh; background-color: #0a0a0b; position: relative;
}
.bg-grid {
  position: fixed; inset: 0; pointer-events: none; z-index: 0;
  background-image:
    linear-gradient(rgba(245, 166, 35, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 166, 35, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}
.content { position: relative; z-index: 1; padding: 20px 24px; }

.no-pipeline {
  padding: 48px; text-align: center;
  border: 1px solid rgba(255,255,255,0.06); border-radius: 8px;
  background: rgba(255,255,255,0.02);
}
.np-title { font-family: var(--font-display); font-size: 16px; font-weight: 700; color: #e8e6e3; margin: 0 0 8px; }
.np-body  { font-family: var(--font-mono); font-size: 12px; color: rgba(232,230,227,0.45); margin: 0; }
.np-link  { color: #f5a623; }

.playground-grid {
  display: grid;
  grid-template-columns: 320px 1fr;
  gap: 16px;
  align-items: start;
}

/* Input col */
.input-col { display: flex; flex-direction: column; gap: 10px; position: sticky; top: 20px; }
.input-header { display: flex; align-items: center; justify-content: space-between; }
.col-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232,230,227,0.3);
}
.mode-toggle { display: flex; gap: 2px; }
.mode-btn {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700; letter-spacing: 0.8px;
  padding: 3px 10px; border-radius: 3px; border: 1px solid rgba(255,255,255,0.08);
  background: transparent; color: rgba(232,230,227,0.3); cursor: pointer; transition: all 0.15s;
}
.mode-btn:hover { color: rgba(232,230,227,0.6); }
.mode-btn.active {
  background: rgba(245,166,35,0.12); border-color: rgba(245,166,35,0.3); color: #f5a623;
}
.mode-hint {
  font-family: var(--font-mono); font-size: 9px; color: rgba(232,230,227,0.2);
  margin: 0; line-height: 1.5;
}
.mode-hint code {
  color: rgba(232,230,227,0.35); background: rgba(255,255,255,0.04);
  padding: 1px 4px; border-radius: 2px;
}
.json-parse-error {
  font-family: var(--font-mono); font-size: 10px; color: #f87171; margin: 0;
}
.text-editor {
  width: 100%; box-sizing: border-box; min-height: 120px; resize: vertical;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px; padding: 12px; font-family: var(--font-body, sans-serif); font-size: 13px;
  color: #e8e6e3; outline: none; line-height: 1.6; transition: border-color 0.15s;
}
.text-editor:focus { border-color: rgba(245,166,35,0.4); }
.text-editor::placeholder { color: rgba(232,230,227,0.15); }

.json-editor {
  width: 100%; box-sizing: border-box; min-height: 200px; resize: vertical;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 6px; padding: 12px; font-family: var(--font-mono); font-size: 12px;
  color: #e8e6e3; outline: none; line-height: 1.6; transition: border-color 0.15s;
}
.json-editor:focus { border-color: rgba(245,166,35,0.4); }
.json-editor::placeholder { color: rgba(232,230,227,0.15); }

.client-row {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 5px;
}
.client-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1px; color: rgba(232,230,227,0.25); white-space: nowrap;
}
.client-value {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.6);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.run-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 11px; border: none; border-radius: 6px; background: #f5a623;
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 1px; color: #0a0a0b; cursor: pointer; transition: opacity 0.15s;
}
.run-btn:hover:not(:disabled) { opacity: 0.88; }
.run-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.run-icon { font-size: 13px; }

.run-error {
  font-family: var(--font-mono); font-size: 10px; color: #f87171;
  padding: 8px 10px; background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.2); border-radius: 4px;
}

/* Webhook opt-in checkbox */
.webhook-toggle {
  display: flex; align-items: center; gap: 0; cursor: pointer;
  padding: 8px 10px;
  background: rgba(245,166,35,0.04);
  border: 1px solid rgba(245,166,35,0.15); border-radius: 5px;
  transition: background 0.15s, border-color 0.15s;
}
.webhook-toggle:hover { background: rgba(245,166,35,0.08); border-color: rgba(245,166,35,0.25); }
.webhook-checkbox {
  appearance: none; -webkit-appearance: none;
  width: 14px; height: 14px; flex-shrink: 0;
  border: 1px solid rgba(245,166,35,0.35); border-radius: 3px;
  background: transparent; cursor: pointer; position: relative;
  transition: background 0.15s, border-color 0.15s;
}
.webhook-checkbox:checked {
  background: #f5a623; border-color: #f5a623;
}
.webhook-checkbox:checked::after {
  content: "✓";
  position: absolute; top: -1px; left: 1px;
  font-size: 10px; font-weight: 700; color: #0a0a0b; line-height: 1;
}
.webhook-toggle-label {
  display: flex; align-items: center; gap: 6px; margin-left: 8px;
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  letter-spacing: 0.6px; color: rgba(245,166,35,0.7);
  user-select: none;
}
.webhook-toggle-icon { font-size: 12px; }

/* Output col */
.output-col { display: flex; flex-direction: column; gap: 12px; min-width: 0; }

.idle-state {
  padding: 48px; text-align: center;
  border: 1px solid rgba(255,255,255,0.06); border-radius: 8px;
  background: rgba(255,255,255,0.015);
}
.idle-icon { font-size: 28px; margin-bottom: 12px; opacity: 0.2; }
.idle-text { font-family: var(--font-mono); font-size: 12px; color: rgba(232,230,227,0.25); margin: 0; }

.running-state {
  padding: 32px; text-align: center;
  border: 1px solid rgba(245,166,35,0.15); border-radius: 8px;
  background: rgba(245,166,35,0.03);
  display: flex; flex-direction: column; align-items: center; gap: 12px;
}
.spinner {
  width: 20px; height: 20px; border: 2px solid rgba(245,166,35,0.2);
  border-top-color: #f5a623; border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }
.running-text { font-family: var(--font-mono); font-size: 11px; color: rgba(232,230,227,0.45); margin: 0; }

.section-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232,230,227,0.3); margin-bottom: 4px;
}

.step-card {
  background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06);
  border-radius: 6px; overflow: hidden;
}
.step-head {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.05);
}
.step-num {
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  background: rgba(245,166,35,0.18); color: #f5a623;
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.step-name  { font-family: var(--font-mono); font-size: 12px; font-weight: 600; color: #e8e6e3; flex: 1; }
.step-loops { font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.35); }
.step-rag-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  padding: 1px 6px; border-radius: 3px;
  background: rgba(96,165,250,0.12); color: #60a5fa; letter-spacing: 0.4px;
}
.step-cost  { font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.25); }

.step-loops-detail {
  display: flex; flex-wrap: wrap; gap: 6px;
  padding: 8px 14px; border-bottom: 1px solid rgba(255,255,255,0.04);
}
.fl-chip {
  font-family: var(--font-mono); font-size: 9px; padding: 2px 8px; border-radius: 3px;
  background: rgba(74,222,128,0.08); color: rgba(74,222,128,0.7);
}
.fl-chip.revised { background: rgba(245,166,35,0.1); color: #f5a623; }

.rag-hits {
  border-top: 1px solid rgba(96,165,250,0.12);
  background: rgba(96,165,250,0.03);
}
.rag-hits-label {
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px;
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.8px; color: #60a5fa; text-transform: uppercase;
}
.rag-hits-icon { font-size: 11px; }
.rag-hit {
  border-top: 1px solid rgba(96,165,250,0.08);
  padding: 8px 14px; display: flex; flex-direction: column; gap: 6px;
}
.rag-hit-meta {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.rag-hit-field {
  font-family: var(--font-mono); font-size: 10px; font-weight: 700; color: #60a5fa;
}
.rag-hit-resolver {
  font-family: var(--font-mono); font-size: 9px; padding: 1px 6px; border-radius: 3px;
  background: rgba(96,165,250,0.1); color: rgba(96,165,250,0.7);
}
.rag-hit-query {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.35);
  font-style: italic;
}
.rag-hit-count {
  font-family: var(--font-mono); font-size: 9px; margin-left: auto;
  color: rgba(232,230,227,0.25);
}
.rag-hit-docs { display: flex; flex-direction: column; gap: 4px; }
.rag-hit-doc {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.55);
  padding: 5px 8px; background: rgba(96,165,250,0.05);
  border-left: 2px solid rgba(96,165,250,0.25); border-radius: 2px;
  line-height: 1.5; white-space: pre-wrap; word-break: break-word;
}
.rag-hit-empty {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.2);
  font-style: italic;
}

.step-output {
  padding: 12px 14px; font-family: var(--font-mono); font-size: 11px;
  color: rgba(232,230,227,0.7); white-space: pre-wrap; word-break: break-word; margin: 0; line-height: 1.6;
}

.final-output-header {
  display: flex; align-items: center; gap: 10px; margin-top: 16px; margin-bottom: 4px;
}
.final-output-header .section-label { margin-bottom: 0; }
.schema-badge {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.6px; padding: 2px 8px; border-radius: 3px;
}
.schema-badge.valid   { background: rgba(74,222,128,0.12); color: #4ade80; }
.schema-badge.invalid { background: rgba(248,113,113,0.12); color: #f87171; }
.final-output {
  padding: 14px; font-family: var(--font-mono); font-size: 12px;
  color: #e8e6e3; white-space: pre-wrap; word-break: break-word; margin: 0;
  background: rgba(245,166,35,0.04); border: 1px solid rgba(245,166,35,0.15);
  border-radius: 6px; line-height: 1.6;
}

.cost-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
}
.cost-item { font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.35); }
.cost-sep  { color: rgba(232,230,227,0.15); }

/* Feedback */
.feedback-section { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 14px; }
.feedback-buttons { display: flex; gap: 10px; margin-top: 8px; }
.fb-btn {
  font-family: var(--font-mono); font-size: 11px; font-weight: 700;
  letter-spacing: 0.6px; padding: 9px 18px; border-radius: 5px; cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.fb-btn.correct {
  background: rgba(74,222,128,0.1); border: 1px solid rgba(74,222,128,0.25); color: #4ade80;
}
.fb-btn.correct:hover, .fb-btn.correct.active {
  background: rgba(74,222,128,0.2); border-color: rgba(74,222,128,0.4);
}
.fb-btn.wrong {
  background: rgba(248,113,113,0.1); border: 1px solid rgba(248,113,113,0.25); color: #f87171;
}
.fb-btn.wrong:hover, .fb-btn.wrong.active {
  background: rgba(248,113,113,0.2); border-color: rgba(248,113,113,0.4);
}
.fb-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.feedback-done {
  font-family: var(--font-mono); font-size: 11px; color: rgba(232,230,227,0.5);
  margin-top: 8px; padding: 10px 14px;
  background: rgba(255,255,255,0.03); border-radius: 5px;
}

.correction-editor { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
.field-label {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1px; color: rgba(232,230,227,0.3);
}
.correction-input {
  width: 100%; box-sizing: border-box; resize: vertical; min-height: 80px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08);
  border-radius: 5px; padding: 10px 12px; font-family: var(--font-mono); font-size: 11px;
  color: #e8e6e3; outline: none; line-height: 1.5; transition: border-color 0.15s;
}
.correction-input:focus { border-color: rgba(245,166,35,0.4); }
.correction-error { font-family: var(--font-mono); font-size: 10px; color: #f87171; margin: 0; }
.correction-submit {
  align-self: flex-start;
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  letter-spacing: 0.8px; padding: 8px 16px; border-radius: 4px; border: none;
  background: #f5a623; color: #0a0a0b; cursor: pointer; transition: opacity 0.15s;
}
.correction-submit:hover:not(:disabled) { opacity: 0.85; }
.correction-submit:disabled { opacity: 0.35; cursor: not-allowed; }

/* Webhook notice */
.webhook-notice {
  display: flex; align-items: center; gap: 12px;
  padding: 10px 14px; border-radius: 5px;
  background: rgba(245, 166, 35, 0.04);
  border: 1px solid rgba(245, 166, 35, 0.15);
}
.webhook-notice-icon { font-size: 14px; flex-shrink: 0; }
.webhook-notice-body { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
.webhook-notice-title {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1px; color: #f5a623;
}
.webhook-notice-text {
  font-family: var(--font-mono); font-size: 10px; color: rgba(232,230,227,0.4);
}
.webhook-notice-text code {
  color: rgba(232,230,227,0.55); background: rgba(255,255,255,0.05);
  padding: 1px 4px; border-radius: 2px;
}
.webhook-notice-link {
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.6px; color: #f5a623; text-decoration: none; white-space: nowrap;
  flex-shrink: 0; transition: opacity 0.15s;
}
.webhook-notice-link:hover { opacity: 0.75; }

/* RAG section */
.rag-section { border-top: 1px solid rgba(255,255,255,0.06); padding-top: 14px; }
.rag-toggle {
  display: flex; align-items: center; gap: 8px; width: 100%;
  background: none; border: none; padding: 0; cursor: pointer; text-align: left;
  font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 1.2px; color: rgba(232,230,227,0.3); transition: color 0.15s;
}
.rag-toggle:hover { color: rgba(232,230,227,0.6); }
.rag-count {
  font-size: 9px; padding: 1px 6px; border-radius: 3px;
  background: rgba(245,166,35,0.12); color: #f5a623;
}
.rag-entries { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
.rag-loading, .rag-empty {
  font-family: var(--font-mono); font-size: 11px; color: rgba(232,230,227,0.25);
  padding: 12px; text-align: center;
}
.rag-entry {
  padding: 10px 12px; background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06); border-radius: 5px;
  display: flex; flex-direction: column; gap: 6px;
}
.rag-meta { display: flex; gap: 6px; }
.rag-source-badge, .rag-conf-badge {
  display: inline-block; font-family: var(--font-mono); font-size: 9px; font-weight: 700;
  letter-spacing: 0.6px; text-transform: uppercase; padding: 1px 6px; border-radius: 3px;
}
.rag-source-badge.auto_learned    { background: rgba(96,165,250,0.12); color: #60a5fa; }
.rag-source-badge.human_correction { background: rgba(74,222,128,0.12); color: #4ade80; }
.rag-source-badge.api_upload      { background: rgba(245,166,35,0.12); color: #f5a623; }
.rag-conf-badge { background: rgba(255,255,255,0.06); color: rgba(232,230,227,0.4); }
.rag-content { font-family: var(--font-mono); font-size: 11px; color: rgba(232,230,227,0.6); margin: 0; line-height: 1.5; }
</style>
